import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useContactInvitationStatus = (contactEmail?: string, organizationId?: string) => {
  return useQuery({
    queryKey: ["contact-invitation-status", contactEmail, organizationId],
    queryFn: async () => {
      if (!contactEmail || !organizationId) {
        return { 
          hasPendingInvitation: false, 
          isExpired: false,
          invitationId: null 
        };
      }

      // Check for active (non-expired) pending invitations for this organization
      const { data: activeInvitation, error: activeError } = await supabase
        .from("user_invitations")
        .select("id, status, expires_at")
        .eq("email", contactEmail)
        .eq("organization_id", organizationId)
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (activeError) {
        console.error("Error checking active invitation status:", activeError);
      }

      // If there's an active invitation, return it
      if (activeInvitation) {
        return {
          hasPendingInvitation: true,
          isExpired: false,
          invitationId: activeInvitation.id,
        };
      }

      // Check for expired pending invitations for this organization
      const { data: expiredInvitation, error: expiredError } = await supabase
        .from("user_invitations")
        .select("id, status, expires_at")
        .eq("email", contactEmail)
        .eq("organization_id", organizationId)
        .eq("status", "pending")
        .lte("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (expiredError) {
        console.error("Error checking expired invitation status:", expiredError);
      }

      // If there's an expired invitation
      if (expiredInvitation) {
        return {
          hasPendingInvitation: true, // Still considered "pending" for resend purposes
          isExpired: true,
          invitationId: expiredInvitation.id,
        };
      }

      // No invitation found
      return {
        hasPendingInvitation: false,
        isExpired: false,
        invitationId: null,
      };
    },
    enabled: !!contactEmail && !!organizationId,
  });
};
